import DataExplorationIcon from '@mui/icons-material/DataExploration';
import { Box, Chip, Stack } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';

import { tabLabelWithCount, tabLabelWithIcon } from '~/components/common/TabCount';
import { TabPanel, useTabs } from '~/components/common/tabs';
import { TabContext, TabStrip } from '~/components/common/tabs/useTabs';
import { ContractAttachmentsTab } from '~/components/contractReview/ContractAttachmentsTab';
import { ContractCfiTab } from '~/components/contractReview/ContractCfiTab';
import { ContractKpisTab } from '~/components/contractReview/ContractKpisTab';
import { ContractPerformanceTab } from '~/components/contractReview/ContractPerformanceTab';
import { ContractRatesTab } from '~/components/contractReview/ContractRatesTab';
import { ContractPropertiesTab } from '~/components/contractReview/detailPage/ContractPropertiesTab';
import { ContractReviewRulesTab } from '~/components/contractReview/detailPage/ContractReviewRulesTab';
import type { ContractReview } from '~/components/contractReview/types';
import { ServiceHistoryTab } from '~/components/serviceHistory/ServiceHistoryTab';
import { serviceHistoryTabLabel } from '~/components/serviceHistory/ServiceHistoryTabLabel';
import { pageBandColor, SURFACE_RADIUS } from '~/theme';

enum Tab {
  Properties = 'Properties',
  Cfi = 'CFI',
  Performance = 'Performance',
  // The tab strip labels this "Service History" since the callbacks sub-tab joined it.
  // The value is what the `tab` search param carries, so it stays as it is rather than
  // breaking links already pointing here.
  VisitsCompletion = 'Maintenance Completion',
  ReviewRules = 'Review Rules',
  Rates = 'Rates',
  Kpis = 'KPIs',
  Attachments = 'Attachments',
}

/**
 * Properties first, not ContractIQ: `ContractCfiTab` fires `cfi_tab_viewed` and
 * starts a poll as soon as it mounts, so landing on it would count every contract
 * anyone opens as a CFI view.
 */
const tabLabels = [
  Tab.Properties,
  Tab.Cfi,
  Tab.Performance,
  Tab.VisitsCompletion,
  Tab.ReviewRules,
  Tab.Rates,
  Tab.Kpis,
  Tab.Attachments,
];

export function ContractTabContainer({
  contract,
  onAddRule,
}: {
  contract: ContractReview;
  /** Opens the contract form on its Invoice Review Rules section. `null` for a reader who can't edit contracts. */
  onAddRule: (() => void) | null;
}) {
  const { context, labelToTabId, selectedTab, selectTab } = useTabs(tabLabels, Tab.Properties);

  // React Router keeps the route mounted when only `:contractId` changes, so reset the
  // selection when a different contract opens.
  const openedContract = useRef(contract.id);
  useEffect(() => {
    if (contract.id === openedContract.current) return;
    openedContract.current = contract.id;
    selectTab(Tab.Properties);
  }, [contract.id, selectTab]);

  const overrideLabels = useMemo(
    () => ({
      [Tab.Properties]: tabLabelWithCount('Property Breakdown', contract.properties.length),
      // CFI is still beta, so the label carries the flag across every CFI state.
      [Tab.Cfi]: (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          ContractIQ
          <Chip
            label="Beta"
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              height: 18,
              fontSize: '0.625rem',
              fontWeight: 600,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
      ),
      [Tab.Performance]: tabLabelWithIcon(
        'Performance',
        <DataExplorationIcon sx={{ fontSize: 20 }} />,
      ),
      [Tab.VisitsCompletion]: serviceHistoryTabLabel(),
      [Tab.ReviewRules]: tabLabelWithCount('AI Invoice Review Rules', contract.reviewRules.length),
      [Tab.Rates]: tabLabelWithCount('Rates', contract.rateSets.length),
      // Cars carrying their own targets. Properties with no cars on the contract
      // contribute no rows, so they are not counted.
      [Tab.Kpis]: tabLabelWithCount(
        'KPIs',
        contract.properties.reduce((total, property) => total + property.cars.length, 0),
      ),
      [Tab.Attachments]: tabLabelWithCount('Attachments', contract.attachments.length),
    }),
    [
      contract.attachments.length,
      contract.properties,
      contract.rateSets.length,
      contract.reviewRules.length,
    ],
  );

  return (
    // `minWidth: 0` here and on the panel row: a flex item defaults to `min-width: auto`,
    // so a data grid wider than the window stretches every ancestor instead of scrolling
    // inside its own viewport.
    <Stack flex={1} minWidth={0}>
      <TabContext.Provider value={context}>
        <TabStrip
          overrideLabels={overrideLabels}
          styles={{
            tabs: {
              backgroundColor: pageBandColor,
              px: { xs: 2, md: 3 },
              borderBottomLeftRadius: SURFACE_RADIUS,
              borderBottomRightRadius: SURFACE_RADIUS,
            },
          }}
        />
        {/* Must be a column flex container: a row one with overflow drops the cross-axis
          (bottom) padding of its scrolled content, clipping each tab's own pb.

          No `overflow: auto`. It never scrolled, and its one live effect was to register
          as the nearest scroll container for `position: sticky`, pinning ContractIQ's
          action bar to a box that scrolls away with the page. */}
        <Box flex={1} minWidth={0} sx={{ display: 'flex', flexDirection: 'column' }}>
          <TabPanel tabId={labelToTabId[Tab.Properties]}>
            <ContractPropertiesTab contract={contract} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Cfi]}>
            {/* Mounted only while selected: `TabPanel` hides its children with `display: none`
              rather than unmounting them, and this tab reports a view and polls for a report
              the moment it mounts. */}
            {selectedTab === labelToTabId[Tab.Cfi] ? (
              <ContractCfiTab contract={contract} wide />
            ) : null}
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Performance]}>
            {/* Mounted only while selected: it reads a date-scoped KPI endpoint over every
              property on the contract. */}
            {selectedTab === labelToTabId[Tab.Performance] ? (
              <ContractPerformanceTab contract={contract} />
            ) : null}
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.VisitsCompletion]}>
            <ServiceHistoryTab
              scope={{ contract: contract.id }}
              alwaysShowProperty
              visitsEmptyMessage="This contract covers no elevators."
              callbacksEmptyMessage="This contract covers no elevators."
            />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.ReviewRules]}>
            <ContractReviewRulesTab contract={contract} onAddRule={onAddRule} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Rates]}>
            <ContractRatesTab contract={contract} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Kpis]}>
            <ContractKpisTab contract={contract} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Attachments]}>
            <ContractAttachmentsTab contract={contract} />
          </TabPanel>
        </Box>
      </TabContext.Provider>
    </Stack>
  );
}
